import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getApptDashboard, getInvoices, getNotifications } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatTime } from '../../utils/formatDateTime';
import PageTour from '../../components/tenant/PageTour';

const TOUR_STEPS = [
  {
    selector: '[data-tour="share-link"]',
    title: 'Seu link de agendamento',
    content: 'Compartilhe este link no Instagram, WhatsApp, cartão de visita. É a porta de entrada pros seus clientes marcarem 24h por dia.',
    placement: 'bottom',
  },
  {
    selector: '[data-tour="stats-grid"]',
    title: 'Números em tempo real',
    content: 'Hoje, semana e mês atualizam conforme os agendamentos entram. Acompanhe a evolução do seu negócio.',
    placement: 'top',
  },
  {
    selector: '[data-tour="new-appt-btn"]',
    title: 'Cadastro manual',
    content: 'Cliente ligou ou apareceu? Cadastre direto por aqui. Agendamentos do link público entram sozinhos.',
    placement: 'bottom',
  },
];

export default function TenantDashboard() {
  const { user, hasPermission, hasFeature } = useAuth();
  const [appts,  setAppts]  = useState(null);
  const [bills,  setBills]  = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    const promises = [
      getApptDashboard().catch(() => null),
      hasFeature('billing') && hasPermission('VIEW_BILLING')
        ? getInvoices({ status:'pending' }).catch(() => null)
        : Promise.resolve(null),
      hasFeature('notifications') && hasPermission('VIEW_NOTIFICATIONS')
        ? getNotifications().catch(() => null)
        : Promise.resolve(null),
    ];
    Promise.all(promises).then(([a, b, n]) => {
      if (a) setAppts(a.data);
      if (b) setBills(b.data);
      if (n) setNotifs(n.data.notifications.slice(0, 5));
    }).catch(() => toast.error('Erro ao carregar dashboard')).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-body" style={{ display:'flex', justifyContent:'center', paddingTop:60 }}><div className="spinner" /></div>;

  const canCreateAppt  = hasPermission('CREATE_APPOINTMENT');
  const canViewAppts   = hasPermission('VIEW_ALL_APPOINTMENTS') || hasPermission('VIEW_OWN_APPOINTMENTS');
  const canViewBilling = hasFeature('billing') && hasPermission('VIEW_BILLING');
  const pendingInvoice = bills?.invoices?.[0];

  const TYPE_ICON = { NEW_APPOINTMENT:'📅', APPOINTMENT_UPDATED:'📝', APPOINTMENT_CANCELLED:'❌', APPOINTMENT_REMINDER:'⏰', NEW_INVOICE:'💰', PAYMENT_CONFIRMED:'✅', INVOICE_OVERDUE:'⚠️' };
  const fmtDate = (d) => {
    if (!d) return '';
    const s = typeof d === 'string' && d.length > 10 ? d.slice(0, 10) : typeof d === 'string' ? d : new Date(d).toISOString().slice(0, 10);
    return new Date(s + 'T12:00:00').toLocaleDateString('pt-BR');
  };

  function timeAgo(d) {
    const s = Math.floor((Date.now()-new Date(d))/1000);
    if (s<60) return 'agora'; if (s<3600) return `${Math.floor(s/60)}min`; if (s<86400) return `${Math.floor(s/3600)}h`; return `${Math.floor(s/86400)}d`;
  }

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Dashboard</h2>
          <p>Bem-vindo de volta, <strong>{user?.name?.split(' ')[0]}</strong> 👋</p>
          <div style={{ marginTop: 8 }}>
            <PageTour tourId="dashboard" steps={TOUR_STEPS} />
          </div>
        </div>
        {canCreateAppt && <Link to="/app/appointments" className="btn btn-primary" data-tour="new-appt-btn">+ Novo Agendamento</Link>}
      </div>

      {/* Alerta de fatura pendente */}
      {canViewBilling && pendingInvoice && (
        <div className="warning-box" style={{ marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span>💰 Você tem uma fatura pendente de <strong>R$ {parseFloat(pendingInvoice.amount).toFixed(2)}</strong> com vencimento em {fmtDate(pendingInvoice.due_date)}</span>
          <Link to="/app/billing" className="btn btn-warning btn-sm" style={{ marginLeft:16 }}>Ver fatura</Link>
        </div>
      )}

      {/* Stats de agendamentos */}
      {appts && (
        <div className="stats-grid" data-tour="stats-grid">
          {[
            { icon:'📅', label:'Hoje',            value:appts.today,               color:'blue'   },
            { icon:'📆', label:'Esta semana',     value:appts.week,                color:'indigo' },
            { icon:'📊', label:'Este mês',        value:appts.month,               color:'amber'  },
            { icon:'⏳', label:'Aguardando',      value:appts.pending,             color:'purple' },
            { icon:'✅', label:'Concluídos/mês',  value:appts.completed_month,     color:'green'  },
            { icon:'👤', label:'Clientes únicos', value:appts.unique_clients_month, color:'cyan'   },
            { icon:'💰', label:'Receita estimada', value: appts.revenue_month > 0 ? `R$ ${parseFloat(appts.revenue_month).toFixed(0)}` : '—', color:'emerald' },
          ].map(c => (
            <div key={c.label} className="stat-card">
              <div className={`stat-icon ${c.color}`}>{c.icon}</div>
              <div>
                <div className="stat-label">{c.label}</div>
                <div className="stat-value">{c.value ?? 0}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Insights automáticos */}
      {appts?.insights?.length > 0 && (
        <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {appts.insights.map((insight, i) => {
            const bg = insight.type === 'success' ? '#dcfce7' : insight.type === 'warning' ? '#fef3c7' : insight.type === 'danger' ? '#fef2f2' : 'var(--gray-50)';
            const border = insight.type === 'success' ? '#86efac' : insight.type === 'warning' ? '#fcd34d' : insight.type === 'danger' ? '#fca5a5' : 'var(--gray-200)';
            const color = insight.type === 'success' ? '#065f46' : insight.type === 'warning' ? '#92400e' : insight.type === 'danger' ? '#991b1b' : 'var(--gray-700)';
            return (
              <div key={i} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '10px 14px', fontSize: 13, color, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{insight.icon}</span>
                <span>{insight.text}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Seu link de agendamento — funcionalidade mais importante, visível sempre */}
      {user?.tenant?.slug && (
        <div data-tour="share-link" style={{
          marginBottom: 20, background: 'var(--surface)', borderRadius: 12,
          border: '1px solid var(--gray-200)', padding: '16px 20px',
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: 32, flexShrink: 0 }}>🔗</div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>
              Sua página de agendamento
            </div>
            <a href={`/agendar/${user.tenant.slug}`} target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--primary)', fontWeight: 600, fontSize: 14, wordBreak: 'break-all' }}>
              atendoapp.com/agendar/{user.tenant.slug}
            </a>
            <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
              Compartilhe com seus clientes via WhatsApp, Instagram ou cartão de visita
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
            <button className="btn btn-sm btn-secondary" onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/agendar/${user.tenant.slug}`);
              toast.success('Link copiado!');
            }}>
              Copiar link
            </button>
            <button className="btn btn-sm btn-secondary" onClick={() => setShowQR(true)}>
              QR Code
            </button>
            <a href={`/agendar/${user.tenant.slug}`} target="_blank" rel="noopener noreferrer"
              className="btn btn-sm btn-primary">
              Abrir página
            </a>
          </div>
        </div>
      )}

      <div className="grid-2">
        {/* Próximos agendamentos */}
        {canViewAppts && appts && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Próximos Agendamentos</span>
              <Link to="/app/appointments" className="btn btn-sm btn-secondary">Ver todos →</Link>
            </div>
            <div className="card-body">
              {!appts.upcoming?.length ? (
                <div className="empty-state" style={{ padding:'24px 0' }}>
                  <div className="empty-icon">📅</div>
                  <h3>Nenhum agendamento futuro</h3>
                  {canCreateAppt && <Link to="/app/appointments" className="btn btn-primary btn-sm" style={{ marginTop:10 }}>Agendar agora</Link>}
                </div>
              ) : (
                <div className="upcoming-list">
                  {appts.upcoming.map(a => (
                    <div key={a.id} className="appt-row">
                      <div className="appt-time">
                        {formatDate(a.datetime, { year: undefined })}
                        <br />{formatTime(a.datetime)}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div className="appt-client">{a.client_name}</div>
                        <div className="appt-meta">{a.owner_name} · {a.duration_minutes}min</div>
                      </div>
                      <span className="badge badge-info" style={{ fontSize:10 }}>Agendado</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notificações recentes */}
        {hasFeature('notifications') && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Notificações Recentes</span>
              <Link to="/app/notifications" className="btn btn-sm btn-secondary">Ver todas →</Link>
            </div>
            <div className="card-body" style={{ padding:0 }}>
              {!notifs.length ? (
                <div className="empty-state" style={{ padding:'24px 0' }}>
                  <div className="empty-icon">🔔</div><h3>Nenhuma notificação</h3>
                </div>
              ) : (
                notifs.map(n => (
                  <div key={n.id} className={`notif-item${!n.read?' unread':''}`} style={{ padding:'12px 16px' }}>
                    <span className="notif-icon">{TYPE_ICON[n.type]||'🔔'}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div className="notif-title">{n.title}</div>
                      <div className="notif-message" style={{ WebkitLineClamp:1, display:'-webkit-box', WebkitBoxOrient:'vertical', overflow:'hidden' }}>{n.message}</div>
                    </div>
                    <span className="notif-time">{timeAgo(n.created_at)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal QR Code */}
      {showQR && user?.tenant?.slug && (
        <div className="modal-overlay" onClick={() => setShowQR(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
            <div className="modal-header">
              <h3>QR Code — Agendamento</h3>
              <button className="modal-close" onClick={() => setShowQR(false)}>✕</button>
            </div>
            <div className="modal-body" id="qr-print-area">
              <div style={{ marginBottom: 16 }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(window.location.origin + '/agendar/' + user.tenant.slug)}`}
                  alt="QR Code de Agendamento"
                  style={{ width: 280, height: 280, borderRadius: 12, border: '1px solid var(--gray-200)' }}
                />
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{user.tenant.name}</div>
              <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 4 }}>Agende pelo celular</div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)', wordBreak: 'break-all' }}>
                atendoapp.com/agendar/{user.tenant.slug}
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center' }}>
              <button className="btn btn-primary" onClick={() => {
                const printWin = window.open('', '_blank', 'width=400,height=600');
                const url = `${window.location.origin}/agendar/${user.tenant.slug}`;
                const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(url)}`;
                printWin.document.write(`<!DOCTYPE html><html><head><title>QR Code - ${user.tenant.name}</title>
                  <style>body{font-family:Arial,sans-serif;text-align:center;padding:40px 20px;margin:0}
                  img{margin:20px auto;display:block;border:2px solid #e5e7eb;border-radius:12px}
                  h1{font-size:24px;margin:0 0 8px}p{color:#6b7280;font-size:14px;margin:4px 0}
                  .url{font-size:11px;color:#9ca3af;word-break:break-all;margin-top:12px}
                  @media print{body{padding:60px 40px}}</style></head>
                  <body><h1>${user.tenant.name}</h1><p>Aponte a câmera do celular para agendar</p>
                  <img src="${qrUrl}" width="300" height="300" alt="QR Code"/>
                  <p class="url">${url}</p>
                  <script>window.onload=function(){window.print();}<\/script></body></html>`);
                printWin.document.close();
              }}>
                Imprimir QR Code
              </button>
              <button className="btn btn-secondary" onClick={() => {
                const url = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&format=png&data=${encodeURIComponent(window.location.origin + '/agendar/' + user.tenant.slug)}`;
                const a = document.createElement('a'); a.href = url; a.download = `qrcode-${user.tenant.slug}.png`; a.click();
              }}>
                Baixar PNG
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
